import React from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CaretDown, X } from "@phosphor-icons/react";
import { TR_PROVINCES, TR_PROVINCE_LIST } from "@/data/tr-provinces";

// Türkçe karakterleri normalize eden filtre fonksiyonu (İ/i, Ş/s, Ç/c vs).
const normalize = (s) =>
  (s || "").toLocaleLowerCase("tr-TR")
    .replace(/ı/g, "i").replace(/i/g, "i")
    .replace(/ş/g, "s").replace(/ç/g, "c")
    .replace(/ğ/g, "g").replace(/ü/g, "u").replace(/ö/g, "o");
const trFilter = (value, search) => normalize(value).includes(normalize(search)) ? 1 : 0;

/**
 * Combobox tarzı Türkiye il/ilçe seçici.
 * Serbest metin girişine de izin verir (defter dışı yerleşim adları için).
 * Props: value, onChange(string), items (opsiyonel, verilmezse TR_PROVINCE_LIST kullanılır),
 *        placeholder, testId
 */
export function ComboBox({ value, onChange, items, placeholder, testId, emptyText = "Sonuç yok" }) {
  const [open, setOpen] = React.useState(false);
  const list = items || TR_PROVINCE_LIST;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="flex gap-1">
          <Input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            data-testid={testId}
            className="rounded-none sm:rounded-sm flex-1"
          />
          <Button
            type="button" variant="outline"
            onClick={(e) => { e.preventDefault(); setOpen((o) => !o); }}
            className="rounded-none sm:rounded-sm border-slate-300 px-2"
            tabIndex={-1}
          >
            <CaretDown size={14} weight="bold" />
          </Button>
        </div>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 w-64 rounded-none sm:rounded-sm" onOpenAutoFocus={(e) => e.preventDefault()}>
        <Command filter={trFilter}>
          <CommandInput placeholder="Ara..." />
          <CommandList className="max-h-60">
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {list.map((it) => (
                <CommandItem
                  key={it}
                  value={it}
                  onSelect={() => { onChange(it); setOpen(false); }}
                >
                  {it}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * İl + İlçe eşli combobox. İl değişince ilçe filtrelenir/temizlenir.
 * Props: city, district, onChange({city, district})
 */
export default function CityDistrictPicker({ city, district, onChange }) {
  const districts = React.useMemo(() => (city && TR_PROVINCES[city]) || [], [city]);

  return (
    <div className="grid grid-cols-2 gap-3">
      <ComboBox
        value={city}
        onChange={(v) => {
          // Yeni ilin ilçe listesinde eski ilçe yoksa sıfırla
          const nextDistricts = TR_PROVINCES[v] || [];
          const newDistrict = nextDistricts.includes(district) ? district : "";
          onChange({ city: v, district: newDistrict });
        }}
        placeholder="İl (Örn: İstanbul)"
        testId="picker-city"
      />
      <ComboBox
        value={district}
        onChange={(v) => onChange({ city, district: v })}
        items={districts.length ? districts : []}
        emptyText={city ? "İlçe bulunamadı" : "Önce il seçin"}
        placeholder={city ? "İlçe seçin veya yazın" : "Önce il seçin"}
        testId="picker-district"
      />
    </div>
  );
}
